import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Banknote, Calculator, Download, FileText, History, RotateCcw, XCircle } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { EmptyState } from '@/components/common/EmptyState'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { PermissionGate } from '@/components/common/PermissionGate'
import { downloadPayrollPaymentSlipPdf } from '@/api/services/payroll-live.service'
import { triggerBlobDownload } from '@/utils/download'
import {
  usePayrollPayment,
  usePayrollPayments,
  useReversePayrollPayment,
} from '@/features/payroll/hooks/usePayrollLive'
import { RejectReasonModal } from './RejectReasonModal'
import type {
  PayrollPaymentItem,
  PayrollPaymentSnapshot,
} from '@/features/payroll/types/payroll.types'

const PAGE_SIZE = 20

/** A reversed live payment stays in history (status 'void' with a reversal time). */
function isReversed(snapshot: PayrollPaymentSnapshot): boolean {
  return snapshot.status === 'void' || !!snapshot.reversedAt
}

function fmt(value: number | undefined | null): string {
  return (value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function periodLabel(period: { year: number; month: number }): string {
  return new Date(Date.UTC(period.year, period.month - 1, 1)).toLocaleString(undefined, {
    month: 'long',
    year: 'numeric',
  })
}

function exportItemsCsv(snapshot: PayrollPaymentSnapshot, items: PayrollPaymentItem[]): void {
  const header = ['Slip', 'Employee', 'Company', 'Hours', 'OT', 'Gross', 'Deductions', 'Tax', 'Net', 'Currency']
  const rows = items.map((item) => [
    item.slipNumber ?? '',
    item.employee.name ?? item.employee.id,
    item.company.name,
    item.regularHours,
    item.overtimeHours,
    item.totals.gross ?? 0,
    item.totals.deductions ?? 0,
    item.totals.tax ?? 0,
    item.totals.netPay ?? 0,
    item.currency,
  ])
  const csv = [header, ...rows]
    .map((row) =>
      row
        .map((cell) => {
          const text = String(cell)
          return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
        })
        .join(','),
    )
    .join('\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = `paid-payroll-${snapshot.payrollMonth.year}-${String(snapshot.payrollMonth.month).padStart(2, '0')}.csv`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

function HistoryList(): React.ReactElement {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = usePayrollPayments({ page, limit: PAGE_SIZE })
  const rows = data?.data ?? []

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Payroll', to: '/payroll' }, { label: 'Paid history' }]}
      icon={History}
      title="Paid Payroll History"
      description="Saved payroll payments, audit totals and slip records"
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
      <WorkspaceToolbar
        actions={
          <Link to="/payroll">
            <Button variant="secondary" size="sm">
              <Calculator className="h-3.5 w-3.5" />
              <span className="ml-1.5">Calculate payroll</span>
            </Button>
          </Link>
        }
      />
      {isLoading ? (
        <TableSkeleton rows={6} cols={6} />
      ) : isError ? (
        <EmptyState icon={XCircle} title="Could not load paid payroll history" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Banknote}
          title="No paid payroll yet"
          description="Paid snapshots will appear here after you pay selected employees."
        />
      ) : (
        <section className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-4 py-3">Period</th>
                  <th className="px-4 py-3 text-right">Employees</th>
                  <th className="px-4 py-3 text-right">Gross</th>
                  <th className="px-4 py-3 text-right">Tax</th>
                  <th className="px-4 py-3 text-right">Net paid</th>
                  <th className="px-4 py-3">Paid at</th>
                  <th className="px-4 py-3 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((snapshot) => (
                  <tr
                    key={snapshot.id}
                    className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-primary/5 dark:border-gray-700/70 dark:hover:bg-primary/10"
                    onClick={() => navigate(`/payroll/history/${snapshot.id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                      {periodLabel(snapshot.payrollMonth)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{snapshot.employeeCount}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt(snapshot.totals.gross)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt(snapshot.totals.tax)}</td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">
                      {snapshot.currency} {fmt(snapshot.totals.netPay)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {new Date(snapshot.paidAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isReversed(snapshot) ? (
                        <Badge variant="neutral">Reversed</Badge>
                      ) : (
                        <Badge variant="success">Paid</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data && (
            <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-700">
              <TablePaginationBar
                page={data.page}
                limit={data.limit}
                total={data.total}
                totalPages={data.totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </section>
      )}
      </div>
    </PageShell>
  )
}

function PaymentDetail({ snapshotId }: { snapshotId: string }): React.ReactElement {
  const { data, isLoading, isError } = usePayrollPayment(snapshotId)
  const [downloadingItemId, setDownloadingItemId] = useState<string | null>(null)
  const [reverseOpen, setReverseOpen] = useState(false)
  const reverseMut = useReversePayrollPayment()

  const handleReverse = (reason: string): void => {
    reverseMut.mutate({ snapshotId, reason }, { onSuccess: () => setReverseOpen(false) })
  }

  const downloadPdf = async (item: PayrollPaymentItem): Promise<void> => {
    setDownloadingItemId(item.id)
    try {
      const blob = await downloadPayrollPaymentSlipPdf(snapshotId, item.id)
      triggerBlobDownload(blob, `salary-slip-${item.slipNumber ?? item.id}.pdf`)
    } finally {
      setDownloadingItemId(null)
    }
  }

  if (isLoading) return <TableSkeleton rows={5} cols={5} />

  if (isError || !data) {
    return (
      <PageShell breadcrumbItems={[{ label: 'Payroll', to: '/payroll' }]} icon={FileText} title="Paid Payroll" className="gap-0" hideHeader>
        <EmptyState icon={XCircle} title="Paid payroll snapshot not found" />
      </PageShell>
    )
  }

  const reversed = isReversed(data)

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Payroll', to: '/payroll' },
        { label: 'Paid history', to: '/payroll/history' },
        { label: periodLabel(data.payrollMonth) },
      ]}
      icon={FileText}
      title={periodLabel(data.payrollMonth)}
      description={`${data.employeeCount} employees paid on ${new Date(data.paidAt).toLocaleString()}`}
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          actions={
            <>
              <Button variant="secondary" size="sm" onClick={() => exportItemsCsv(data, data.items)}>
                <Download className="h-3.5 w-3.5" />
                <span className="ml-1.5">Export slips CSV</span>
              </Button>
              <PermissionGate module="payroll" action="finalize">
                {!reversed && (
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={reverseMut.isPending}
                    onClick={() => setReverseOpen(true)}
                  >
                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                    Reverse payment
                  </Button>
                )}
              </PermissionGate>
            </>
          }
        />
        {reversed && (
          <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
            <RotateCcw className="mt-0.5 h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400" />
            <div className="min-w-0 text-sm">
              <p className="font-medium text-gray-800 dark:text-gray-200">Payment reversed</p>
              {data.reversalReason && (
                <p className="mt-1 text-gray-700 dark:text-gray-300">{data.reversalReason}</p>
              )}
              {data.reversedAt && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {new Date(data.reversedAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        )}
        <section className="grid gap-3 sm:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="text-xl font-semibold tabular-nums">{data.employeeCount}</div>
            <div className="mt-1 text-xs text-gray-500">Employees</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="text-xl font-semibold tabular-nums">{fmt(data.totals.gross)}</div>
            <div className="mt-1 text-xs text-gray-500">Gross</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="text-xl font-semibold tabular-nums">{fmt(data.totals.tax)}</div>
            <div className="mt-1 text-xs text-gray-500">Tax</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="text-xl font-semibold tabular-nums text-green-700">
              {data.currency} {fmt(data.totals.netPay)}
            </div>
            <div className="mt-1 text-xs text-gray-500">Net paid</div>
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Salary Slip Records
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-4 py-3">Slip</th>
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3 text-right">Hours</th>
                  <th className="px-4 py-3 text-right">Gross</th>
                  <th className="px-4 py-3 text-right">Tax</th>
                  <th className="px-4 py-3 text-right">Net</th>
                  <th className="px-4 py-3 text-right">PDF</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100 last:border-0 dark:border-gray-700/70">
                    <td className="px-4 py-3 font-mono text-xs">{item.slipNumber}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                      {item.employee.name ?? item.employee.id}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{item.company.name}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {item.regularHours} / OT {item.overtimeHours}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt(item.totals.gross)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt(item.totals.tax)}</td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">
                      {item.currency} {fmt(item.totals.netPay)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {reversed ? (
                        <span className="text-xs text-gray-500 dark:text-gray-400">Reversed</span>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={downloadingItemId !== null}
                          onClick={() => void downloadPdf(item)}
                        >
                          <Download className="mr-1.5 h-3.5 w-3.5" />
                          {downloadingItemId === item.id ? 'Preparing...' : 'PDF'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <RejectReasonModal
        open={reverseOpen}
        onClose={() => setReverseOpen(false)}
        onSubmit={handleReverse}
        title="Reverse payment"
        description="Records that this payment was reversed and reopens the month for these employees so attendance, leave and pay can change again. Employees are not notified. Say why (for example, paid to the wrong account)."
        submitLabel="Reverse payment"
        loading={reverseMut.isPending}
      />
    </PageShell>
  )
}

export function PayrollPaymentHistoryPage(): React.ReactElement {
  const { snapshotId } = useParams<{ snapshotId?: string }>()
  if (snapshotId) return <PaymentDetail snapshotId={snapshotId} />
  return <HistoryList />
}
